
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, AsyncSubject, firstValueFrom } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Constants } from './constants';
import { IdentityRepository, IdentityUser, Menu } from '@model/identity';
import { environment } from 'src/environments/environment';
import { IdentityOperations } from '@model/identity/operations';
import { dateWithTimeToString, fromMills } from '@common/date-utils';
import { SESSION_KEY_IDENTITY_DNI, SESSION_KEY_USUARIO } from 'src/app/common/constants-ejb';

/**
 * Implementación del servicio de identidad
 */
@Injectable({
  providedIn: 'root'
})
export class IdentityServiceImpl extends IdentityRepository {
  private readonly baseUrl = environment.backend.baseUrl;

  /** representa el usuario logueado en el sistema */
  private user?: IdentityUser = undefined;

  /** Subject para notificar la informacion del usuario  */
  private authState = new Subject<IdentityUser>();

  /**
   *  Almacena una referencia hacia el observable que obtiene la información del usuario y lo marca de tipo share para que
   *  no se haga una misma peticion multiples veces
   */
  private observerLoginInfo?: Observable<IdentityUser>;

  /**
   * servicio para hacer peticiones http
   */
  private readonly http = inject(HttpClient);

  /**
   * Esta función se encarga de enviar por el authState el usuario, ya sea que lo recupere del sessionStorage o del servicio
   * expuesto en el backend
   */
  refreshUserInfo(): void {
    this.observerLoginInfo = this.http
      .get<IdentityUser>(
        `${this.baseUrl}/user/current`
      )
      .pipe(
        share({
          connector: () => new AsyncSubject(),
          resetOnError: false,
          resetOnComplete: false,
          resetOnRefCountZero: false
        })
      );

    this.observerLoginInfo.subscribe((user) => {
      this.user = user;

      if (this.user) {
        // Persistir DNI del SSO en sessionStorage para acceso síncrono
        if (user.dni) {
          sessionStorage.setItem(SESSION_KEY_IDENTITY_DNI, user.dni);
        }
        this.authState.next(user);
      }
    });
  }

  /**
   * Devuelve un Observable en el cual se notificará el usuario en la sesión.
   *
   * @returns devuelve un stream donde se puede obtener la información del usuario logueado
   */
  getUserInfo(): Observable<IdentityUser> {
    if (!this.observerLoginInfo) {
      this.refreshUserInfo();
    }

    return this.authState.asObservable();
  }

/**
 * Obtiene el menú del usuario desde la API /api/menu
 */
  getMenuFromApi(): Observable<Menu[]> {
    return this.http.get<Menu[]>( `${this.baseUrl}/menu`);
  }

  /* metodo */
  getUserLastAccessFromApi(): Observable<string> {
    return this.http.get<string>( `${this.baseUrl}/user/lastAccess`);
  }

  /**
   * Devuelve el menu asociado a el usuario
   *
   * @returns devuelve el menu del usuario
   */
  getUserMenu(): Promise<Menu[]> {
    if (this.user) {
      const menu = this.user.menu;
      return Promise.resolve(menu ? JSON.parse(menu) as Menu[] : []);
    }
    return firstValueFrom(
      this.getUserInfo().pipe(map(user => {
        const menu = user?.menu;
        return menu ? JSON.parse(menu) as Menu[] : [];
      }))
    );
  }

  /**
   * Invoca función que devuelve el ultmio acceso del usuario 
   */
  getUserLastAccess(): Promise<string> {
    return firstValueFrom(this.getUserLastAccessFromApi());
  }

  /**
   *
   * Con esta funcion se puede obtener el valor LoggedInYet o el formulario de login enviado por el sistema de seguridad
   *
   * @returns devuelve el html que representa el formulario de login
   */
  getLoginForm(): Observable<string> {
    const options = { responseType: 'text' as const };
    return this.http.post(
      `${this.baseUrl}/security/status`,
      null,
      options
    );
  }

  isLoggedIn(): boolean {
    return this.user != null;
  }

  /**
   *
   * Esta funcion comprueba si un usuario esta logueado en el sistema
   *
   * @returns indica si el usuario tiene acceso a un recurso específico
   */
  isAuthorized(resource: string): Observable<boolean> {
    const body = {
      method: 'GET',
      path: resource
    };
    return this.http.post<boolean>(
      `${this.baseUrl}/user/hasAccess`,
      body
    );
  }

  /**
   *
   * Esta funcion invoca el logout completo delly sistema, aqui si se cierra la sesión el usuario debería ingresar sus
   * credenciales si desea volver a ingresar.
   *
   * @returns indica si el usuario tiene acceso a un recurso específico
   */
  fullLogout(fullUrlLogout: string): void {
    // Limpiar sessionStorage de identidad SSO y datos de usuario
    sessionStorage.removeItem(SESSION_KEY_IDENTITY_DNI);
    sessionStorage.removeItem(SESSION_KEY_USUARIO);
    this.clearObserverForLogin();
    window.location.href = fullUrlLogout || '';
  }

  /**
   *
   * Esta función se encarga de eliminar la referencia del Observable que devuelve la información del usuario logueado
   * de esta forma se puede hacer refresh de dicha información solicitandola al backend
   *
   * @returns indica si el usuario tiene acceso a un recurso específico
   */
  clearObserverForLogin(): void {
    this.observerLoginInfo = undefined;
    this.user = undefined;
  }

  /**
   * Logout parcial: elimina la sesión backend y limpia el estado local
   */
  logout(): Promise<void> {
    return firstValueFrom(
      this.http.get(
        `${this.baseUrl}/logout`,
        { responseType: 'text' }
      )
    ).then((logoutUrl) => {
      sessionStorage.removeItem(SESSION_KEY_IDENTITY_DNI);
      sessionStorage.removeItem(SESSION_KEY_USUARIO);
      this.clearObserverForLogin();
      if (logoutUrl) {
        window.location.href = logoutUrl;
      }
    });
  }
}
